/**
 * PHK WebMCP tools
 *
 * Registers a small set of imperative WebMCP tools so AI agents (Chrome 150+
 * with WebMCP) can search the catalog, read product details, and manage the
 * cart through the Shopify AJAX API instead of scraping the page.
 *
 * Complements the declarative toolname/tooldescription attributes on the
 * theme's forms. No-ops in browsers without WebMCP.
 *
 * Tools:
 *   find_parts     read-only  search products by part number / keyword
 *   get_product    read-only  full variant list for one product handle
 *   get_cart       read-only  current cart contents
 *   add_variant_to_cart  write  add a variant id to the cart
 */
(function () {
  'use strict';

  var ctx =
    (typeof document !== 'undefined' && document.modelContext) ||
    (typeof navigator !== 'undefined' && navigator.modelContext) ||
    null;

  if (!ctx || typeof ctx.registerTool !== 'function') return;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  if (root.charAt(root.length - 1) !== '/') root += '/';

  function money(cents) {
    if (typeof cents !== 'number') return null;
    return Math.round(cents) / 100;
  }

  function stripHtml(html, max) {
    if (!html) return '';
    var div = document.createElement('div');
    div.innerHTML = html;
    var text = (div.textContent || '').replace(/\s+/g, ' ').trim();
    if (max && text.length > max) text = text.slice(0, max - 1) + '\u2026';
    return text;
  }

  function fetchJson(url, options) {
    return fetch(url, options).then(function (res) {
      if (!res.ok) {
        return res.text().then(function (body) {
          var err = new Error('Request failed (' + res.status + ')');
          err.body = body;
          throw err;
        });
      }
      return res.json();
    });
  }

  function variantSummary(v) {
    return {
      variant_id: v.id,
      title: v.title,
      sku: v.sku || null,
      price: money(v.price),
      available: !!v.available
    };
  }

  function productSummary(p) {
    var variants = Array.isArray(p.variants) ? p.variants.map(variantSummary) : [];
    return {
      title: p.title,
      handle: p.handle,
      url: p.url || root + 'products/' + p.handle,
      vendor: p.vendor || null,
      product_type: p.type || p.product_type || null,
      available: typeof p.available === 'boolean' ? p.available : variants.some(function (v) { return v.available; }),
      price_from: money(p.price_min != null ? p.price_min : p.price),
      variants: variants
    };
  }

  function notifyCartChanged(cartResponse) {
    try {
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'phk-webmcp', cartData: cartResponse });
      }
    } catch (e) {
      /* non-fatal */
    }
  }

  function register(tool, options) {
    try {
      var p = ctx.registerTool(tool, options);
      if (p && typeof p.catch === 'function') p.catch(function () {});
    } catch (e) {
      /* WebMCP unavailable or rejected registration; stay silent */
    }
  }

  register({
    name: 'find_parts',
    description:
      'Search the Parts and Hardware catalog of Andersen window and door replacement parts. ' +
      'Accepts a part number (for example 9007598), product name, or keyword and returns matching ' +
      'products with prices, SKUs, availability, and page URLs.',
    inputSchema: {
      type: 'object',
      properties: {
        query: { type: 'string', description: 'Part number, product name, or keyword to search for.' },
        limit: { type: 'integer', minimum: 1, maximum: 10, description: 'Maximum results to return (1-10). Default 5.' }
      },
      required: ['query']
    },
    annotations: { readOnlyHint: true, untrustedContentHint: false, consequentialHint: false },
    execute: function (input, extra) {
      var q = String((input && input.query) || '').trim();
      if (!q) return Promise.resolve(JSON.stringify({ error: 'query is required' }));
      var limit = Math.min(Math.max(parseInt((input && input.limit) || 5, 10) || 5, 1), 10);
      var url =
        root +
        'search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product' +
        '&resources[limit]=' + limit +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor,tag';
      return fetchJson(url, { signal: extra && extra.signal })
        .then(function (data) {
          var products = (((data || {}).resources || {}).results || {}).products || [];
          return JSON.stringify({
            query: q,
            count: products.length,
            results_url: root + 'search?q=' + encodeURIComponent(q) + '&options[prefix]=last',
            products: products.map(productSummary)
          });
        })
        .catch(function (err) {
          return JSON.stringify({ error: err.message });
        });
    }
  });

  register({
    name: 'get_product',
    description:
      'Get full details for one product on partsandhardware.com by its handle (the last part of the ' +
      'product URL). Returns description, price, and every variant with its variant_id, SKU, and availability. ' +
      'Use the variant_id with add_variant_to_cart.',
    inputSchema: {
      type: 'object',
      properties: {
        handle: { type: 'string', description: 'Product handle, for example compact-casement-operator-handle.' }
      },
      required: ['handle']
    },
    annotations: { readOnlyHint: true, untrustedContentHint: false, consequentialHint: false },
    execute: function (input, extra) {
      var handle = String((input && input.handle) || '').trim().replace(/^\/?products\//, '').replace(/[?#].*$/, '');
      if (!handle) return Promise.resolve(JSON.stringify({ error: 'handle is required' }));
      return fetchJson(root + 'products/' + encodeURIComponent(handle) + '.js', { signal: extra && extra.signal })
        .then(function (p) {
          var summary = productSummary(p);
          summary.description = stripHtml(p.description, 1200);
          summary.options = Array.isArray(p.options) ? p.options : [];
          return JSON.stringify(summary);
        })
        .catch(function (err) {
          return JSON.stringify({ error: err.message || 'Product not found' });
        });
    }
  });

  register({
    name: 'get_cart',
    description: 'Read the current shopping cart: line items with variant_id, SKU, quantity, and price, plus the subtotal.',
    inputSchema: { type: 'object', properties: {} },
    annotations: { readOnlyHint: true, untrustedContentHint: false, consequentialHint: false },
    execute: function (input, extra) {
      return fetchJson(root + 'cart.js', { signal: extra && extra.signal })
        .then(function (cart) {
          return JSON.stringify({
            item_count: cart.item_count,
            subtotal: money(cart.items_subtotal_price),
            total: money(cart.total_price),
            currency: cart.currency,
            cart_url: root + 'cart',
            checkout_url: root + 'checkout',
            items: (cart.items || []).map(function (i) {
              return {
                variant_id: i.variant_id,
                title: i.product_title,
                variant_title: i.variant_title || null,
                sku: i.sku || null,
                quantity: i.quantity,
                price: money(i.price),
                line_total: money(i.final_line_price),
                url: i.url
              };
            })
          });
        })
        .catch(function (err) {
          return JSON.stringify({ error: err.message });
        });
    }
  });

  register({
    // Named differently from the declarative add_to_cart form tool on product
    // pages; WebMCP tool names must be unique per document.
    name: 'add_variant_to_cart',
    description:
      'Add a product variant to the shopping cart by its numeric variant_id (from find_parts or get_product). ' +
      'Does not start checkout; the shopper reviews the cart before paying.',
    inputSchema: {
      type: 'object',
      properties: {
        variant_id: { type: 'integer', description: 'Shopify variant ID to add.' },
        quantity: { type: 'integer', minimum: 1, description: 'Units to add. Default 1.' }
      },
      required: ['variant_id']
    },
    annotations: { readOnlyHint: false, untrustedContentHint: false, consequentialHint: false },
    execute: function (input, extra) {
      var id = parseInt(input && input.variant_id, 10);
      var qty = Math.max(parseInt((input && input.quantity) || 1, 10) || 1, 1);
      if (!id) return Promise.resolve(JSON.stringify({ error: 'variant_id is required' }));
      return fetchJson(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: id, quantity: qty }] }),
        signal: extra && extra.signal
      })
        .then(function (res) {
          return fetchJson(root + 'cart.js').then(function (cart) {
            notifyCartChanged(cart);
            var added = (res.items || [])[0] || {};
            return JSON.stringify({
              added: {
                variant_id: added.variant_id || id,
                title: added.product_title || null,
                sku: added.sku || null,
                quantity: qty
              },
              cart_item_count: cart.item_count,
              cart_total: money(cart.total_price),
              cart_url: root + 'cart'
            });
          });
        })
        .catch(function (err) {
          var message = err.message;
          try {
            var body = JSON.parse(err.body || '{}');
            if (body.description) message = body.description;
          } catch (e) {
            /* keep default message */
          }
          return JSON.stringify({ error: message });
        });
    }
  });
})();
